import React, { useState, useEffect, useRef, useCallback } from 'react'

const Transcript = ({ transcriptUrl, currentTime, onSeek, onError }) => {
  const [transcriptData, setTranscriptData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const scrollbarRef = useRef(null)
  const listRef = useRef(null)

  // Fetch transcript data
  useEffect(() => {
    if (!transcriptUrl) return

    const fetchTranscript = async () => {
      try {
        setIsLoading(true)
        setError(null)
        
        const response = await fetch(transcriptUrl)
        if (!response.ok) {
          throw new Error(`Failed to fetch transcript: ${response.status}`)
        }
        
        const data = await response.json()
        setTranscriptData(data)
        
        // Add a small delay to ensure DOM is rendered
        setTimeout(() => {
        }, 100)
      } catch (err) {
        console.error('Error fetching transcript:', err)
        setError(err.message)
        if (onError) {
          onError(err)
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchTranscript()
  }, [transcriptUrl, onError])

  // Highlight current segment and auto-scroll
  useEffect(() => {
    if (!transcriptData || !listRef.current) return

    // Validate currentTime
    if (typeof currentTime !== 'number' || isNaN(currentTime) || currentTime < 0) {
      return
    }

    // Add a small delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(() => {
      try {
        // Find segments within the scrollable container
        const segments = listRef.current?.querySelectorAll('.transcript-segment')
        
        if (!segments || segments.length === 0) {
          return
        }
        
        let activeSegment = null

        segments.forEach((segment, index) => {
          const start = parseFloat(segment.dataset.start)
          const end = parseFloat(segment.dataset.end)
          
          // Remove previous active class
          segment.classList.remove('active')
          
          // Check if current time is within this segment (with some tolerance)
          if (currentTime >= start && currentTime <= end) {
            segment.classList.add('active')
            activeSegment = segment
          }
        })

        // If no active segment found, try to find the closest one
        if (!activeSegment && segments.length > 0) {
          let closestSegment = null
          let minDistance = Infinity
          
          segments.forEach((segment, index) => {
            const start = parseFloat(segment.dataset.start)
            const end = parseFloat(segment.dataset.end)
            const midPoint = (start + end) / 2
            const distance = Math.abs(currentTime - midPoint)
            
            if (distance < minDistance) {
              minDistance = distance
              closestSegment = segment
            }
          })
          
          if (closestSegment) {
            closestSegment.classList.add('active')
            activeSegment = closestSegment
          }
        }

        // Auto-scroll to active segment using native scrolling
        if (activeSegment && scrollbarRef.current) {
          try {
            const container = scrollbarRef.current
            const segmentTop = activeSegment.offsetTop
            const containerHeight = container.clientHeight
            const segmentHeight = activeSegment.clientHeight
            
            const scrollTop = segmentTop - (containerHeight / 2) + (segmentHeight / 2)
            
            // Use native scrolling
            container.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            })
          } catch (error) {
            console.warn('Transcript scroll error:', error)
          }
        }
      } catch (error) {
        console.warn('Error updating transcript highlighting:', error)
      }
    }, 50) // Small delay to ensure DOM is ready

    return () => clearTimeout(timeoutId)
  }, [currentTime, transcriptData])

  const handleSegmentClick = useCallback((startTime) => {
    try {
      onSeek(startTime)
    } catch (error) {
      console.warn('Error seeking to time:', error)
    }
  }, [onSeek])

  const formatTime = useCallback((seconds) => {
    try {
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    } catch (error) {
      console.warn('Error formatting time:', error)
      return '0:00'
    }
  }, [])

  const decodeHtmlEntities = useCallback((text) => {
    try {
      const textarea = document.createElement('textarea')
      textarea.innerHTML = text
      return textarea.value
    } catch (error) {
      console.warn('Error decoding HTML entities:', error)
      return text
    }
  }, [])

  const formatTimeForVTT = useCallback((seconds) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    const ms = Math.floor((seconds % 1) * 1000)
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
  }, [])

  if (isLoading) {
    return (
      <div className="video-modal__player--transcript--loading">
        Loading transcript...
      </div>
    )
  }

  if (error) {
    return (
      <div className="video-modal__player--transcript--error">
        Error loading transcript: {error}
      </div>
    )
  }

  if (!transcriptData || !transcriptData.transcript) {
    return (
      <div className="video-modal__player--transcript--error">
        No transcript available
      </div>
    )
  }

  // Validate transcript data structure
  if (!Array.isArray(transcriptData.transcript) || transcriptData.transcript.length === 0) {
    return (
      <div className="video-modal__player--transcript--error">
        Invalid transcript data format
      </div>
    )
  }

  // Validate that segments have required properties
  const hasValidSegments = transcriptData.transcript.every(segment => 
    typeof segment.start === 'number' && 
    typeof segment.duration === 'number' && 
    typeof segment.text === 'string'
  )

  if (!hasValidSegments) {
    return (
      <div className="video-modal__player--transcript--error">
        Transcript segments have invalid format
      </div>
    )
  }

  return (
    <div className="video-modal__transcript--content" style={{ flex: 1, overflow: 'hidden' }}>
      <div 
        ref={scrollbarRef}
        className="video-modal__transcript--list"
        style={{ 
          height: '100%', 
          overflowY: 'auto',
          padding: 0,
          margin: 0,
          fontSize: '14px'
        }}
      >
        <ul ref={listRef}>
          {transcriptData.transcript.map((segment, index) => (
            <li 
              key={index}
              className="transcript-segment"
              data-start={segment.start}
              data-end={segment.start + segment.duration}
              onClick={() => handleSegmentClick(segment.start)}
            >
              <span className="transcript-time">
                {formatTime(segment.start)}
              </span>
              <span className="transcript-text">
                {decodeHtmlEntities(segment.text)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default Transcript 